import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/lib/i18n";
import { api } from "@/lib/api";
import { ArrowRight, Clock } from "lucide-react";
import SEO from "@/components/SEO";

export default function BlogPage() {
  const { t, language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getBlogPosts().then(setPosts).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : "https://www.pruvajacuzzi.com";
  const lx = (field) => field?.[language] || field?.en || "";
  const formatDate = (d) => new Date(d).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div data-testid="blog-page">
      <SEO
        title="Blog & Resources - Jacuzzi & Spa Guides for Dubai"
        description="Expert guides on jacuzzi wellness benefits, rooftop and balcony installation in Dubai, spa maintenance in the UAE climate, and choosing the right model."
        keywords="jacuzzi blog dubai, spa guides uae, hydrotherapy articles, jacuzzi installation guide, hot tub maintenance tips"
        canonical={`${baseUrl}/blog`}
        breadcrumbs={[
          { name: "Home", url: baseUrl },
          { name: "Blog", url: `${baseUrl}/blog` }
        ]}
      />

      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20" style={{ background: "linear-gradient(135deg, #F8F9FA 0%, #F1F3F5 100%)" }}>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <p className="text-sm uppercase tracking-widest text-amber-700 font-medium mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
            Pruva Jacuzzi
          </p>
          <h1 data-testid="blog-title" className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 mb-6">
            {t("blogPage.pageTitle")}
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl" style={{ fontFamily: "'Inter', sans-serif" }}>
            {t("blogPage.pageSubtitle")}
          </p>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[16/10] bg-slate-200" />
                  <div className="p-6 space-y-3"><div className="h-5 bg-slate-200 w-3/4" /><div className="h-4 bg-slate-200 w-1/2" /></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map(post => (
                <Link key={post.slug} to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`}
                  className="group bg-white overflow-hidden hover:shadow-xl transition-shadow duration-300 border border-slate-100 flex flex-col">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={post.image} alt={lx(post.title)} loading="lazy" width="500" height="313" decoding="async"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs uppercase tracking-wider text-amber-700 bg-amber-50 px-2 py-1 font-medium">{lx(post.category)}</span>
                      <span className="flex items-center gap-1 text-xs text-slate-400" style={{ fontFamily: "'Inter', sans-serif" }}>
                        <Clock className="w-3 h-3" /> {post.read_time} {t("blogPage.minRead")}
                      </span>
                    </div>
                    <h2 className="text-lg font-medium text-slate-900 mb-2 leading-snug" style={{ fontFamily: "'Inter', sans-serif" }}>{lx(post.title)}</h2>
                    <p className="text-sm text-slate-500 leading-relaxed mb-4 flex-1" style={{ fontFamily: "'Inter', sans-serif" }}>{lx(post.excerpt)}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400" style={{ fontFamily: "'Inter', sans-serif" }}>{formatDate(post.date)}</span>
                      <span className="inline-flex items-center gap-2 text-sm text-slate-800 uppercase tracking-widest font-medium group-hover:gap-3 transition-all duration-300">
                        {t("blogPage.readArticle")} <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-slate-50 border-t border-slate-100">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h2 className="text-3xl md:text-4xl font-medium text-slate-900 tracking-tight mb-4">{t("blogPage.ctaTitle")}</h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-8" style={{ fontFamily: "'Inter', sans-serif" }}>{t("blogPage.ctaText")}</p>
          <Link to="/contact" data-testid="blog-cta-button"
            className="inline-block bg-slate-800 text-white px-10 py-4 text-sm uppercase tracking-widest font-medium hover:bg-slate-700 transition-colors duration-300">
            {t("blogPage.ctaButton")}
          </Link>
        </div>
      </section>
    </div>
  );
}
